import { Button, Dialog, DialogBody, DialogFooter, useDisclosure, Text, List, ListItem } from '@bitrise/bitkit';
import { manageExceededLimitsPagePath } from '@/utils/monolithApiRouteService';
import { useMutateUserMetadata } from '@/hooks/api/useUserMetadata';
import useCreditSubscription from '@/hooks/api/useCreditSubscription';
import { Account } from '@/models/Account';

const PlanDowngradeDialog = ({ account }: { account: Account }) => {
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const shouldShowDialog = Boolean(account?.downgradeNotifications?.dialog);

  const { data: subscription } = useCreditSubscription(account, { enabled: shouldShowDialog });
  const limits = subscription?.exceededScheduledLimits;

  const { mutate } = useMutateUserMetadata({ transform: false });

  if (!account || !shouldShowDialog || !limits) {
    return null;
  }

  const onDialogClose = () => {
    mutate({ [`scheduled_downgrade_dialog_notification_on_${account.slug}`]: false });
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={onDialogClose} title="Your plan change will be effective in 5 days">
      <DialogBody>
        <Text mb="32">
          By switching to the new plan, you will exceed some of its limits. To continue using Bitrise after, please
          review the changes you need to make:
        </Text>
        <List variant="unstyled">
          {limits.owners && (
            <ListItem>
              <strong>Owner:</strong> {limits.owners.count} (of {limits.owners.limit})
            </ListItem>
          )}
          {limits.teamMembers && (
            <ListItem>
              <strong>Team member:</strong> {limits.teamMembers.count} (of {limits.teamMembers.limit})
            </ListItem>
          )}
          {limits.uniqueStepsExceeded && (
            <ListItem>
              <strong>Steps per project limit</strong> (in {limits.uniqueStepsExceeded}{' '}
              {limits.uniqueStepsExceeded > 1 ? 'projects' : 'project'})
            </ListItem>
          )}
          {limits.privateApps && (
            <ListItem>
              <strong>Private projects:</strong> {limits.privateApps.count} (of {limits.privateApps.limit})
            </ListItem>
          )}
        </List>
      </DialogBody>
      <DialogFooter>
        <Button as="a" href={manageExceededLimitsPagePath(account.slug)}>
          Manage exceeded limits
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default PlanDowngradeDialog;
